import { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import { deleteUpdateNote, getUpdateNotes } from "../services/UpdateNotesService";
import UpdateNoteForm from "../components/UpdateNotes/UpdateNoteForm";
import { AppModal } from "../components/AppModal";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { EmptyState } from "../components/EmptyState";
import { StaffPageHeader } from "../components/StaffPageHeader";
import { useAccount } from "../Context/AccountProvider";
import { MegaphoneFill } from "react-bootstrap-icons";

const PAGE_SIZE = 10;

// ── Module-level stable refs ──────────────────────────────────────────────────

const annFilter = (a, q) =>
  a.Title.toLowerCase().includes(q) ||
  new Date(a.Date).toLocaleDateString().includes(q) ||
  a.Content?.toLowerCase().includes(q);

const ANN_SORTERS = {
  title: a => a.Title.toLowerCase(),
  date:  a => new Date(a.Date).getTime(),
};

// ── Hook ──────────────────────────────────────────────────────────────────────

function useAnnTableControls(records) {
  const [query,      setQuery]      = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [page,       setPage]       = useState(1);
  const [sortKey,    setSortKey]    = useState('date');
  const [sortDir,    setSortDir]    = useState('desc');

  const yearFiltered = useMemo(() => {
    if (!yearFilter) return records;
    const y = parseInt(yearFilter, 10);
    return records.filter(a => new Date(a.Date).getFullYear() === y);
  }, [records, yearFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? yearFiltered.filter(a => annFilter(a, q)) : yearFiltered;
  }, [yearFiltered, query]);

  const sorted = useMemo(() => {
    const getter = ANN_SORTERS[sortKey];
    if (!getter) return filtered;
    return [...filtered].sort((a, b) => {
      const av = getter(a), bv = getter(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setPage(1); }, [query, sortKey, sortDir, yearFilter]);

  function handleSort(key) {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage  = Math.min(page, pageCount);
  const start     = (safePage - 1) * PAGE_SIZE;

  return {
    query, setQuery,
    yearFilter, setYearFilter,
    page: safePage, setPage,
    pageCount,
    pageItems:     sorted.slice(start, start + PAGE_SIZE),
    totalFiltered: sorted.length,
    total:         records.length,
    sortKey, sortDir, handleSort,
  };
}

// ── Shared table UI ───────────────────────────────────────────────────────────

function SortableHeader({ label, colKey, sortKey, sortDir, onSort }) {
  const active = colKey === sortKey;
  return (
    <th
      className={`sam-th-sortable${active ? ' sam-th-active' : ''}`}
      onClick={() => onSort(colKey)}
    >
      {label}
      <span className="sam-sort-icon">
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </th>
  );
}

function Pagination({ page, pageCount, onPage }) {
  if (pageCount <= 1) return null;
  return (
    <div className="sam-pagination">
      <button className="sam-page-btn" onClick={() => onPage(1)}          disabled={page === 1}>«</button>
      <button className="sam-page-btn" onClick={() => onPage(page - 1)}   disabled={page === 1}>‹</button>
      <span className="sam-page-info">Page {page} of {pageCount}</span>
      <button className="sam-page-btn" onClick={() => onPage(page + 1)}   disabled={page === pageCount}>›</button>
      <button className="sam-page-btn" onClick={() => onPage(pageCount)}  disabled={page === pageCount}>»</button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function StaffAnnouncementManagement() {
  const { toast } = useAccount();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const {
    query, setQuery,
    yearFilter, setYearFilter,
    page, setPage,
    pageCount,
    pageItems,
    totalFiltered,
    total,
    sortKey, sortDir, handleSort,
  } = useAnnTableControls(announcements);

  const availableYears = useMemo(() => {
    const years = new Set(announcements.map(a => new Date(a.Date).getFullYear()));
    return [...years].sort((a, b) => b - a);
  }, [announcements]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await getUpdateNotes();
        if (!cancelled) setAnnouncements(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function openCreate() {
    setEditTarget(null);
    setShowModal(true);
  }

  function openEdit(announcement) {
    setEditTarget(announcement);
    setShowModal(true);
  }

  function handleSaved({ id, title, date, content }) {
    setAnnouncements(prev => {
      const existing = prev.find(a => a.ID === id);
      if (existing) {
        return prev.map(a =>
          a.ID === id ? { ...a, Title: title, Date: date, Content: content } : a
        );
      }
      return [{ ID: id, Title: title, Date: date, Content: content }, ...prev];
    });
    const isEdit = editTarget !== null;
    setShowModal(false);
    setEditTarget(null);
    toast('success', isEdit ? 'Announcement Updated' : 'Announcement Created',
      isEdit ? 'The announcement has been saved.' : 'The new announcement has been published.');
  }

  async function handleDelete(id) {
    try {
      await deleteUpdateNote(id);
      setAnnouncements(prev => prev.filter(a => a.ID !== id));
      setDeleteConfirmId(null);
      toast('success', 'Announcement Deleted', 'The announcement has been removed.');
    } catch {
      toast('error', 'Delete Failed', 'Could not delete the announcement. Please try again.');
    }
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="sam-error">Failed to load announcements.</p>;

  const sortProps = { sortKey, sortDir, onSort: handleSort };

  return (
    <div className="sam-page">
      <StaffPageHeader
        icon={<MegaphoneFill />}
        title="Announcements"
        actions={<Button className="sam-btn-new" onClick={openCreate}>New Announcement</Button>}
      />

      {announcements.length === 0 ? (
        <EmptyState message="No announcements yet." />
      ) : (
        <>
          <div className="sam-controls">
            <input
              type="text"
              className="sam-search-input"
              placeholder="Search by title, content, or date…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <select
              className="sam-year-select"
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
            >
              <option value="">All years</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <span className="sam-result-count">
              {totalFiltered !== total ? `${totalFiltered} of ${total}` : total}
              {' '}result{total !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="sam-table-wrap">
            <table className="sam-table">
              <thead>
                <tr>
                  <SortableHeader label="Title" colKey="title" {...sortProps} />
                  <SortableHeader label="Date"  colKey="date"  {...sortProps} />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="sam-no-results">No results match your search.</td>
                  </tr>
                ) : pageItems.map(a => (
                  <tr key={a.ID}>
                    <td className="sam-title">{a.Title}</td>
                    <td className="sam-date">{new Date(a.Date).toLocaleDateString()}</td>
                    <td className="sam-actions">
                      <LinkContainer exact to={`/updates/${a.ID}/${a.Title.replaceAll(' ', '-')}`}>
                        <button className="sam-link-btn">View</button>
                      </LinkContainer>
                      <button className="sam-link-btn" onClick={() => openEdit(a)}>Edit</button>
                      <button
                        className="sam-link-btn sam-link-btn--danger"
                        onClick={() => setDeleteConfirmId(a.ID)}
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sam-table-footer">
            <Pagination page={page} pageCount={pageCount} onPage={setPage} />
          </div>
        </>
      )}

      {deleteConfirmId !== null && (
        <div className="sam-delete-overlay" onClick={() => setDeleteConfirmId(null)}>
          <div className="sam-delete-confirm" onClick={e => e.stopPropagation()}>
            <p>Delete this announcement? This cannot be undone.</p>
            <div className="sam-delete-actions">
              <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => handleDelete(deleteConfirmId)}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      <AppModal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <AppModal.Header closeButton>
          <AppModal.Title>{editTarget ? 'Edit Announcement' : 'New Announcement'}</AppModal.Title>
        </AppModal.Header>
        <AppModal.Body>
          <UpdateNoteForm announcement={editTarget} onSaved={handleSaved} />
        </AppModal.Body>
      </AppModal>
    </div>
  );
}
